import React from 'react';
import { Form, Schema, Input, Loader } from 'rsuite';
import type { FormInstance } from 'rsuite';
import { useCartQuery } from '@/api/useCartQuery';
import { useUpdateCart } from '@/api/useUpdateCart';


export default function CheckoutEmail() {
  const formRef = React.useRef<FormInstance>(null);
  const { data: cart, isLoading } = useCartQuery();
  const updateCart = useUpdateCart();

  type FormValue = { email?: string };
  const [formValue, setFormValue] = React.useState<FormValue>({ email: cart?.email ?? '' });

  React.useEffect(() => {
    setFormValue({ email: cart?.email ?? '' });
  }, [cart?.email]);

  const { StringType } = Schema.Types;
  const model = React.useMemo(() => Schema.Model({
    email: StringType().isEmail('Please provide a valid email').isRequired('Email is required'),
  }), []);

  const tryUpdate = React.useCallback(async (email: string) => {
    // Validate the form before sending
    if (!formRef.current) return;
    const valid = await formRef.current.check();
    if (!valid) return;

    // Avoid redundant updates
    if (email === (cart?.email ?? '')) return;

    await updateCart.mutateAsync({ email } as any);
  }, [cart?.email, updateCart]);

  if (isLoading) {
    return (
      <div style={{ padding: 12 }}>
        <Loader />
      </div>
    );
  }

  return (
    <Form
      ref={formRef}
      model={model}
      formValue={formValue}
      onChange={(v) => {
        const next = v as FormValue;
        setFormValue(next);
      }}
      fluid
    >
      <Form.Group controlId="email">
        <Form.ControlLabel>Email</Form.ControlLabel>
        <Form.Control
          name="email"
          accepter={Input}
          type="email"
          autoComplete="email"
          onBlur={() => {
            const email = (formValue.email ?? '').trim();
            if (email) tryUpdate(email);
          }}
        />
      </Form.Group>
    </Form>
  );
}
